import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import type { AccessGroup } from '@/shared/access';

import { useAccessGroups, useMoveAccessGroup } from '../../hooks';
import { formatGroupPath, groupParentOptions } from '../../lib/groupHierarchy';

interface MoveGroupDialogProps {
  /** The group being re-parented; `null` closes the dialog. */
  group: AccessGroup | null;
  onClose: () => void;
}

/**
 * The "top level" sentinel for the parent selector. Deliberately NOT `''`: MUI's `Select` treats an
 * empty-string value as its own "nothing selected" state and never displays a matching `MenuItem`'s
 * label for it (see `Select`'s `value` prop doc — "Providing an empty string will select no options"),
 * so a real, non-empty sentinel is what makes "Top level (no parent)" actually render as selected.
 */
const TOP_LEVEL_VALUE = '__top-level__';

/**
 * `PUT /groups/{groupId}/parent` rejects an invalid move with a stable `ApiError.code`
 * (adr-merchant-portal-016): `ACCESS_GROUP_CYCLE` (the target parent is the group itself or one of its
 * own descendants) or `ACCESS_GROUP_BUILT_IN_NOT_NESTABLE` (the built-in All-staff group can't be
 * nested). Matched on the code — not the message — so recognising the case survives a wording change or
 * localisation; any other code falls back to the backend's own message, same as `CreateGroupDialog`.
 */
const ACCESS_GROUP_CYCLE = 'ACCESS_GROUP_CYCLE';
const ACCESS_GROUP_BUILT_IN_NOT_NESTABLE = 'ACCESS_GROUP_BUILT_IN_NOT_NESTABLE';

/** The translation key for a recognised move-rejection code, or `null` for anything else. */
function moveErrorKey(code: string): 'moveGroup.errors.cycle' | 'moveGroup.errors.builtIn' | null {
  if (code === ACCESS_GROUP_CYCLE) return 'moveGroup.errors.cycle';
  if (code === ACCESS_GROUP_BUILT_IN_NOT_NESTABLE) return 'moveGroup.errors.builtIn';
  return null;
}

/**
 * Re-parents an existing department (adr-merchant-portal-016) — moves it, and its whole subtree, under a
 * different parent, or promotes it to the top level. Every member below it starts inheriting the new
 * ancestors' access instead of the old one, which is the point of the operation, not a side effect.
 *
 * <p>The parent selector is the same one `CreateGroupDialog` uses, minus the group's own subtree: a
 * group can never become its own descendant's parent, so offering that choice would only round-trip to
 * the server's cycle guard.
 */
export function MoveGroupDialog({ group, onClose }: MoveGroupDialogProps) {
  const { t } = useTranslation('access');
  const groups = useAccessGroups();
  const move = useMoveAccessGroup();

  const [parentId, setParentId] = useState<string>(TOP_LEVEL_VALUE);

  // The dialog stays mounted (only `group` toggles between a value and `null`), so re-sync the draft to
  // the group's CURRENT parent every time a new group is opened for moving.
  useEffect(() => {
    if (group) setParentId(group.parentId ?? TOP_LEVEL_VALUE);
  }, [group]);

  if (!group) return null;

  const parentOptions = groupParentOptions(groups.data ?? [], group.id);
  const error: NormalizedApiError | null = move.isError ? (move.error ?? null) : null;
  const errorKey = error ? moveErrorKey(error.code) : null;

  const close = () => {
    move.reset();
    onClose();
  };

  return (
    <Dialog open onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{t('moveGroup.title', { name: group.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <DialogContentText>
            {t('moveGroup.helper', { currentPath: formatGroupPath(group) })}
          </DialogContentText>
          {error && <Alert severity="error">{errorKey ? t(errorKey) : error.message}</Alert>}
          <TextField
            select
            label={t('moveGroup.parentLabel')}
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            fullWidth
          >
            <MenuItem value={TOP_LEVEL_VALUE}>{t('moveGroup.noParent')}</MenuItem>
            {parentOptions.map((option) => (
              <MenuItem key={option.id} value={option.id} sx={{ pl: 2 + (option.depth ?? 0) * 2 }}>
                {formatGroupPath(option)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={move.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={move.isPending}
          onClick={() =>
            move.mutate(
              { groupId: group.id, parentId: parentId === TOP_LEVEL_VALUE ? undefined : parentId },
              { onSuccess: close },
            )
          }
        >
          {t('moveGroup.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
